import { Component, computed, effect, inject, input, signal, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AvBoolCellComponent } from '../av-bool-cell/av-bool-cell.component';
import { AvDialogComponent } from '../av-dialog/av-dialog.component';
import { AvTableCopyCellComponent } from '../table-copy-cell/table-copy-cell.component';
import { COMMON_IMPORTS } from '../../common-imports';
import {
  AvTableCellValue,
  AvTableColumn,
  AvTableCopyCell,
  AvTableDialogConfig,
  AvTableLinkCell,
  AvTableProgressCell,
  AvTableRow,
} from '../../Interfaces/av-table-card.interface';

@Component({
  selector: 'av-table-card',
  standalone: true,
  imports: [COMMON_IMPORTS, AvBoolCellComponent, AvTableCopyCellComponent],
  templateUrl: './av-table-card.component.html',
  styleUrls: ['./av-table-card.component.scss', '../../../../../assets/theme/cutom/_av-table.scss'],
})
export class AvTableCard implements OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly paginatorIntl = inject(MatPaginatorIntl);
  private langChangeSubscription?: Subscription;

  readonly titleKey = input.required<string>();
  readonly icon = input.required<string>();
  readonly columns = input<AvTableColumn[]>([]);
  readonly rows = input<AvTableRow[]>([]);
  readonly emptyTextKey = input<string>('shared.general.labels.no_data');
  readonly countLabelKey = input<string | null>(null);
  readonly pageSize = input<number>(5);
  readonly trackByKey = input<string | null>(null);
  readonly truncateAfter = input<number>(20);

  readonly filterText = signal('');

  readonly displayedColumns = computed(() => this.columns().map((column) => column.key));
  readonly filteredRows = computed<AvTableRow[]>(() => {
    const term = this.filterText().trim().toLowerCase();
    if (!term) return this.rows();
    return this.rows().filter((row) => this.rowMatches(row, term));
  });
  readonly count = computed(() => this.filteredRows().length);

  readonly sort = signal<Sort>({ active: '', direction: '' });
  readonly pageIndex = signal(0);

  readonly sortedRows = computed<AvTableRow[]>(() => {
    const sort = this.sort();
    const rows = [...this.filteredRows()];
    if (!sort.active || !sort.direction) return rows;

    const column = this.columns().find((c) => c.key === sort.active);
    if (!column) return rows;

    return rows.sort((a, b) => {
      const dir = sort.direction === 'asc' ? 1 : -1;
      const aVal = this.sortValue(a, column);
      const bVal = this.sortValue(b, column);

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      return aVal > bVal ? dir : -dir;
    });
  });

  readonly totalPages = computed(() => {
    const total = this.sortedRows().length;
    const size = this.pageSize();
    return size > 0 && total > 0 ? Math.ceil(total / size) : 0;
  });

  readonly paginatedRows = computed<AvTableRow[]>(() => {
    const size = this.pageSize();
    if (size <= 0) return this.sortedRows();
    const start = this.pageIndex() * size;
    return this.sortedRows().slice(start, start + size);
  });

  readonly showPagination = computed(() => this.totalPages() > 1);
  readonly showFilter = computed(() => this.showPagination() || this.filterText().trim().length > 0);

  constructor() {
    this.initializePagination();
    this.initializeFilter();
    this.updatePaginatorLabels();
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.updatePaginatorLabels();
    });
  }

  ngOnDestroy(): void {
    this.langChangeSubscription?.unsubscribe();
  }

  initializePagination() {
    effect(() => {
      const total = this.totalPages();
      const page = this.pageIndex();
      if (total > 0 && page > total - 1) {
        this.pageIndex.set(total - 1);
      }
      if (total === 0 && page !== 0) {
        this.pageIndex.set(0);
      }
    });
  }

  initializeFilter() {
    effect(() => {
      this.filterText();
      this.pageIndex.set(0);
    });
  }

  onSortChange(sort: Sort) {
    this.sort.set(sort);
    this.pageIndex.set(0);
  }

  onPageChange(event: PageEvent) {
    this.pageIndex.set(event.pageIndex);
  }

  setFilter(value: string) {
    this.filterText.set(value);
  }

  clearFilter() {
    this.filterText.set('');
  }

  trackByRow = (index: number, row: AvTableRow) => {
    const key = this.trackByKey();
    if (key && row[key] !== undefined) return row[key];
    return index;
  };

  formatText(value: AvTableCellValue): string {
    if (value === null || value === undefined || value === '') {
      return this.translate.instant('shared.general.labels.na');
    }
    if (typeof value === 'number') {
      return String(value);
    }
    if (typeof value === 'string') {
      return this.translate.instant(value as string);
    }
    return this.translate.instant('shared.general.labels.na');
  }

  formatDisplayText(value: AvTableCellValue): string {
    const text = this.formatText(value);
    const limit = this.truncateAfter();
    if (limit > 0 && text.length > limit) {
      return `${text.slice(0, limit)}...`;
    }
    return text;
  }

  isTruncatedText(value: AvTableCellValue): boolean {
    if (value === null || value === undefined || value === '') return false;
    const text = this.formatText(value);
    const limit = this.truncateAfter();
    return limit > 0 && text.length > limit;
  }

  asBool(value: AvTableCellValue): boolean | null {
    return typeof value === 'boolean' ? value : null;
  }

  asProgressCell(value: AvTableCellValue): AvTableProgressCell | null {
    if (!value || typeof value !== 'object') return null;
    if ('percent' in value) return value as AvTableProgressCell;
    return null;
  }

  asLinkCell(value: AvTableCellValue): AvTableLinkCell | null {
    if (!value || typeof value !== 'object') return null;
    if ('href' in value || 'onClick' in value || 'icon' in value || 'text' in value) {
      return value as AvTableLinkCell;
    }
    return null;
  }

  asCopyCell(value: AvTableCellValue): AvTableCopyCell | null {
    if (!value || typeof value !== 'object') return null;
    if ('content' in value) return value as AvTableCopyCell;
    return null;
  }

  linkLabel(cell: AvTableLinkCell | null, fallbackKey: string): string {
    if (cell?.text) return cell.text;
    return this.translate.instant(fallbackKey);
  }

  linkAriaLabel(cell: AvTableLinkCell | null, fallbackKey: string): string {
    if (cell?.ariaLabel) return this.translate.instant(cell.ariaLabel);
    return this.translate.instant(fallbackKey);
  }

  handleLinkClick(cell: AvTableLinkCell | null) {
    if (!cell) return;
    if (cell.dialog) {
      this.openDialog(cell.dialog);
      return;
    }
    if (cell.onClick) {
      cell.onClick();
    }
  }

  handleAnchorClick(event: Event, cell: AvTableLinkCell | null) {
    event.preventDefault();
    this.handleLinkClick(cell);
  }

  private sortValue(row: AvTableRow, column: AvTableColumn): string | number | boolean | null {
    const value = row[column.key];
    if (value === null || value === undefined) return null;

    switch (column.type) {
      case 'bool':
        return typeof value === 'boolean' ? (value ? 1 : 0) : null;
      case 'progress':
        return this.asProgressCell(value)?.percent ?? null;
      case 'link':
        return this.asLinkCell(value)?.text ?? null;
      case 'copy':
        return this.asCopyCell(value)?.content ?? null;
      default:
        if (typeof value === 'number' || typeof value === 'string') return value;
        return null;
    }
  }

  private openDialog(dialog: AvTableDialogConfig) {
    if (!dialog?.entries?.length) return;
    const title = dialog.titleKey ? this.translate.instant(dialog.titleKey) : (dialog.title ?? '');

    this.dialog.open(AvDialogComponent, {
      data: { title, entries: dialog.entries, type: 'alert' },
      panelClass: 'av-dialog',
    });
  }

  private rowMatches(row: AvTableRow, term: string): boolean {
    const parts: string[] = [];
    const seen = new WeakSet<object>();

    const collect = (value: unknown) => {
      if (value === null || value === undefined) return;
      const valueType = typeof value;
      if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') {
        parts.push(String(value));
        return;
      }
      if (Array.isArray(value)) {
        value.forEach(collect);
        return;
      }
      if (valueType === 'object') {
        const record = value as Record<string, unknown>;
        if (seen.has(record)) return;
        seen.add(record);
        Object.values(record).forEach(collect);
      }
    };

    collect(row);
    return parts.join(' ').toLowerCase().includes(term);
  }

  private updatePaginatorLabels() {
    this.paginatorIntl.itemsPerPageLabel = this.translate.instant('shared.general.pagination.items_per_page');
    this.paginatorIntl.nextPageLabel = this.translate.instant('shared.general.pagination.next_page');
    this.paginatorIntl.previousPageLabel = this.translate.instant('shared.general.pagination.previous_page');
    this.paginatorIntl.firstPageLabel = this.translate.instant('shared.general.pagination.first_page');
    this.paginatorIntl.lastPageLabel = this.translate.instant('shared.general.pagination.last_page');
    this.paginatorIntl.getRangeLabel = (page, pageSize, length) => {
      if (length === 0 || pageSize === 0) {
        return this.translate.instant('shared.general.pagination.range_label_empty', { total: length });
      }
      const startIndex = page * pageSize;
      const endIndex = Math.min(startIndex + pageSize, length);
      return this.translate.instant('shared.general.pagination.range_label', {
        start: startIndex + 1,
        end: endIndex,
        total: length,
      });
    };
    this.paginatorIntl.changes.next();
  }
}
